import { Button, Dropdown } from "antd";
import { DownOutlined, GlobalOutlined } from "@ant-design/icons";
import { FiLogOut } from "react-icons/fi";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getDefaultAuthenticatedPath } from "modules/appModuleAccess";
import useAuthStore from "stores/useAuthStore";
import { preloadNamespacesForRoute, preloadRemainingNamespaces } from "i18n";
import { isColorDark, resolveBackgroundColorValue } from "utils/styleUtils";
import "./MobileHeader.css";

const LANGUAGE_LABELS = {
  en: "English",
  en_proman: "English",
  es: "Español",
  es_emcasa: "EMCASA",
  es_csg: "CSG",
};

const languageMenuItemsBase = [
  { key: "en", label: "English" },
  { key: "en_proman", label: "English" },
  { key: "es", label: "Español" },
  { key: "es_emcasa", label: "EMCASA" },
  { key: "es_csg", label: "CSG" },
];

const IS_OFFLINE = process.env.REACT_APP_MODE_OFFLINE === "true";
const DEFAULT_HEADER_BRAND_NAME = "HORUZ";
const DEFAULT_HEADER_BACKGROUND_COLOR = "#ffffff";

function resolveHeaderBranding() {
  const brandName = process.env.REACT_APP_HEADER_BRAND_NAME?.trim();
  
  // In offline mode the brand name is prioritized over the logo since we can't
  // actually fetch the image and the value that could get configured is always the brand name
  if (IS_OFFLINE) {
    if (brandName) return { type: "name", value: brandName };
    return { type: "logo", value: `/assets/images/149MKM_trans_blue.png?ts=${Date.now()}` };
  }

  // In online mode we prioritize the logo if configured, falling back to the brand name and then to the default brand name
  const logoUrl = process.env.REACT_APP_HEADER_LOGO_URL?.trim();
  if (logoUrl) return { type: "logo", value: logoUrl };

  if (brandName) return { type: "name", value: brandName };

  return { type: "name", value: DEFAULT_HEADER_BRAND_NAME };
};

const headerBranding = resolveHeaderBranding();
const headerBackgroundColor = resolveBackgroundColorValue(
  process.env.REACT_APP_HEADER_BACKGROUND_COLOR,
  DEFAULT_HEADER_BACKGROUND_COLOR
);
const isHeaderBackgroundDark = isColorDark(headerBackgroundColor);
const availableLanguages = (process.env.REACT_APP_AVAILABLE_LANGUAGES || "en")
  .split(",")
  .map((lang) => lang.trim())
  .filter(Boolean);
const languageMenuItems = languageMenuItemsBase.filter((item) => availableLanguages.includes(item.key));

export default function MobileHeader({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const userData = useAuthStore((s) => s.userData);
  const language = i18n.language || "en";
  const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false);
  const homePath = getDefaultAuthenticatedPath(userData);

  const handleLanguageChange = useCallback(
    async ({ key }) => {
      localStorage.setItem("i18nextLng", key);
      setLanguageMenuOpen(false);

      try {
        await preloadNamespacesForRoute(location.pathname, key);
        await i18n.changeLanguage(key);
        void preloadRemainingNamespaces(key);
      } catch (error) {
        console.error("Language change failed:", error);
        await i18n.changeLanguage(key);
      }
    },
    [i18n, location.pathname]
  );

  return (
    <div
      className="mobile-app-header"
      data-theme={isHeaderBackgroundDark ? "dark" : "light"}
      style={{ background: headerBackgroundColor }}
    >
      <div className="mobile-app-header-left">
        <button
          className="mobile-app-header-logo-btn"
          onClick={() => navigate(homePath)}
          type="button"
          aria-label="Go to home"
        >
          {headerBranding.type === "logo" ? (
            <img src={headerBranding.value} alt="Brand logo" className="mobile-app-header-logo" />
          ) : (
            <span className="mobile-app-header-brand-text">{headerBranding.value}</span>
          )}
        </button>
      </div>

      <div className="mobile-app-header-actions">
        <Dropdown
          menu={{ items: languageMenuItems, onClick: handleLanguageChange }}
          trigger={["click"]}
          open={isLanguageMenuOpen}
          onOpenChange={setLanguageMenuOpen}
        >
          <button
            type="button"
            className="mobile-app-header-icon-btn mobile-app-header-language-btn"
            aria-haspopup="menu"
            aria-expanded={isLanguageMenuOpen}
            aria-label="Select language"
          >
            <span className="mobile-app-header-language-content">
              <span className="mobile-app-header-language-icon-slot" aria-hidden>
                <GlobalOutlined className="mobile-app-header-language-globe" />
              </span>
              <span className="mobile-app-header-language-label">{LANGUAGE_LABELS[language] ?? language}</span>
              <span className="mobile-app-header-language-caret-slot" aria-hidden>
                <DownOutlined
                  className={`mobile-app-header-language-caret${isLanguageMenuOpen ? " is-open" : ""}`}
                />
              </span>
            </span>
          </button>
        </Dropdown>

        <Button type="text" className="mobile-app-header-icon-btn mobile-app-header-logout-btn" onClick={onLogout}>
          <FiLogOut size={18} />
        </Button>
      </div>
    </div>
  );
}
